import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Update } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { of, race } from 'rxjs';
import { catchError, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { MonaTask } from '@mona/models';
import { ChangeLogAction, ChangeLogSelectors, mergeChangesOnModelId } from '@mona/pdms/data-access-changelog';
import { isEmptyEncounterId, withCurrentEncounterId } from '@mona/pdms/data-access-combined';
import { notEmpty } from '@mona/shared/utils';
import { selectRouteParam } from '@mona/store';
import { TaskListState } from '../../entities';
import {
    TaskListApi,
    createTasksList,
    getCompleteOnDemandMedPrescriptions,
    getUpdateTasksToComplete,
} from '../../infrastructure';
import { TaskListActions } from '../actions/task-list.actions';

/**
 * Task ListEffects effects
 */
@Injectable()
export class TaskListEffects {
    /**
     * Load task list effect
     */
    loadTaskList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TaskListActions.loadTasks),
            withCurrentEncounterId(),
            // switchMap: cancels stale in-flight request when a new loadTasks arrives.
            // Safe because upstream debounce (400ms + 1s timer) prevents rapid-fire.
            // Reconcile's loadTasks (at t=6s) cancels the WS-triggered load if
            // still in-flight, ensuring fresh data is always fetched.
            switchMap(([, encounterId]) =>
                race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() => TaskListActions.loadTasksCancelled({ reason: 'encounter-cleared' })),
                    ),
                    this.taskListApi.getTaskListData(encounterId).pipe(
                        take(1),
                        switchMap(taskIdsLists =>
                            this.store.select(selectRouteParam('encounterId')).pipe(
                                take(1),
                                map(routeEncounterId =>
                                    routeEncounterId === encounterId
                                        ? TaskListActions.loadTasksSuccess({
                                              data: taskIdsLists,
                                              tasks: createTasksList(taskIdsLists),
                                          })
                                        : TaskListActions.loadTasksCancelled({ reason: 'encounter-changed' }),
                                ),
                            ),
                        ),
                        catchError(error => of(TaskListActions.loadTasksFailure({ error }))),
                    ),
                ),
            ),
        ),
    );

    onPersistChanges$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(this.store.select(ChangeLogSelectors.getChangesMap).pipe(notEmpty())),
                tap(([, changesMap]) => {
                    const updateTasks: Update<MonaTask>[] = getUpdateTasksToComplete(changesMap);
                    if (updateTasks.length) {
                        this.store.dispatch(TaskListActions.updateTasks({ updateTasks }));
                    }
                }),
                tap(([, changesMap]) => {
                    const completedOnDemandMedPrIds = getCompleteOnDemandMedPrescriptions(
                        changesMap['MedicationAdministration'],
                    );

                    if (completedOnDemandMedPrIds.length) {
                        this.store.dispatch(
                            TaskListActions.upsertCompleteOnDemandMedPrescriptions({ completedOnDemandMedPrIds }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     *
     * @param store
     * @param actions$
     * @param taskListApi
     */
    constructor(
        private store: Store<{ taskList: TaskListState }>,
        private actions$: Actions,
        private taskListApi: TaskListApi,
    ) {}
}
